import { objectFromEntries } from '@athena-lib/object-utils';
import type { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
import type { CheckoutErrors } from './Checkout.svelte';
import type { CheckoutActions, CheckoutState } from './utils';

type AddressKeys =
  | 'given-name'
  | 'family-name'
  | 'address-line1'
  | 'address-line2'
  | 'postal-code'
  | 'address-level2'
  | 'address-level1';

export type AddressFields<Type extends 'billing' | 'shipping'> = {
  [key in AddressKeys as `${Type} ${key}`]: string;
};
export type CustomerFormFields = AddressFields<'billing'> &
  AddressFields<'shipping'> & {
    'billing country': string;
    email: string;
    tel: string;
    organization: string;
    'vat-number': string;
    'is-company': boolean;
    'shipping-same-as-billing': boolean;
    reference: string;
    comment: string;
  };
export type CustomerForm = {
  [K in keyof CustomerFormFields]: {
    error?: string;
    label: string;
    value: CustomerFormFields[K];
    onchange: () => void;
    validate: (silenceReporting?: boolean) => boolean;
  };
};
export type CustomerFormSdkInterface = {
  [K in keyof CustomerFormFields]: {
    initial: () => CustomerFormFields[K];
    commit: () => Promise<{ success: boolean; translatedErrorCode?: string }>;
    isValid?: ValidationFunction<CustomerFormFields[K]>;
  };
};

/* Validators */

type ValidationFunction<T> = (
  value: T,
  translations: ReturnType<typeof getCommonTranslations>,
  _getLocalFormValues: () => {
    [K in keyof CustomerForm]: CustomerForm[K]['value'];
  },
) => string | undefined;

const requiredAddressField =
  (type: 'billing' | 'shipping'): ValidationFunction<string> =>
  (value, translations, getLocalFormValues) => {
    const shippingSameAsBilling = getLocalFormValues()['shipping-same-as-billing'];
    const validator =
      type === 'billing' && shippingSameAsBilling ? undefined : requiredFieldValidator;
    return validator?.(value, translations, getLocalFormValues);
  };

const requiredFieldValidator: ValidationFunction<string> = (value, translations) => {
  if (!value.trim()) {
    return translations.requiredField;
  }
};

const validEmail: ValidationFunction<string> = (email, translations, _getLocalFormValues) => {
  if (!email.includes('@') || !(email.length > 2)) {
    return translations.invalidEmail;
  }
};

const telNrValidator: ValidationFunction<string> = (value, translations, _getLocalFormValues) => {
  if (!value.match(/^\+?[\d-\s]{5,30}$/)) {
    return translations.invalidPhoneNumber;
  }
};

const organizationNameValidator: ValidationFunction<string> = (
  value,
  translations,
  getLocalFormValues,
) => {
  if (!getLocalFormValues()['is-company']) {
    return undefined;
  }
  if (!value.trim()) {
    return translations.requiredField;
  }
};

const vatNrValidator: ValidationFunction<string> = (value, translations, getLocalFormValues) => {
  if (!getLocalFormValues()['is-company']) {
    return undefined;
  }
  if (!value.trim()) {
    return translations.requiredField;
  }
  const regexp = /^[A-Za-z]{2}[A-Za-z0-9]{6,13}$/;
  if (!value.match(regexp)) {
    return translations.vatNumberFormat;
  }
};
type GetLocalFormValues = () => {
  [K in keyof CustomerForm]: CustomerForm[K]['value'];
};

/* SDK -> Form interfaces */
const getAndSetFieldsFromSdk = (
  type: 'billing' | 'shipping',
  checkoutActions: CheckoutActions,
  checkoutState: CheckoutState,
  getLocalFormValues: GetLocalFormValues,
) => {
  const committer =
    type === 'billing' ? checkoutActions.setBillingAddress : checkoutActions.setAddress;

  const fields = (
    type === 'billing' ? checkoutState.customer.billingAddress : checkoutState.customer.address
  ) as typeof checkoutState.customer.billingAddress;

  return {
    fields,
    setFields: (): Promise<{ success: boolean; message?: string }> => {
      return committer({
        firstName: getLocalFormValues()[`${type} given-name`],
        lastName: getLocalFormValues()[`${type} family-name`],
        address1: getLocalFormValues()[`${type} address-line1`],
        address2: getLocalFormValues()[`${type} address-line2`],
        city: getLocalFormValues()[`${type} address-level2`],
        stateOrProvince: getLocalFormValues()[`${type} address-level1`],
        postalCode: getLocalFormValues()[`${type} postal-code`],
        ...(type === 'billing' ? { countryCode: getLocalFormValues()[`billing country`] } : {}),
      });
    },
  };
};

const orEmpty = (value: string | undefined) => value ?? '';

const getAddressToSdkInterfaces = (
  checkoutState: CheckoutState,
  checkoutActions: CheckoutActions,
  getLocalFormValues: GetLocalFormValues,
) => {
  const entries = (['billing', 'shipping'] as const).flatMap((type) => {
    const { fields, setFields } = getAndSetFieldsFromSdk(
      type,
      checkoutActions,
      checkoutState,
      getLocalFormValues,
    );

    const standardProps = {
      commit: setFields,
      isValid: requiredAddressField(type),
    } as const;
    const getInitialValue = (
      key: Exclude<keyof Exclude<typeof fields, undefined>, boolean | undefined>,
    ): string => orEmpty(fields?.[key] as string | undefined);

    return [
      [`${type} given-name`, { initial: () => getInitialValue('firstName'), ...standardProps }],
      [`${type} family-name`, { initial: () => getInitialValue('lastName'), ...standardProps }],
      [`${type} address-line1`, { initial: () => getInitialValue('address1'), ...standardProps }],
      [`${type} address-line2`, { initial: () => getInitialValue('address2'), commit: setFields }],
      [`${type} address-level2`, { initial: () => getInitialValue('city'), ...standardProps }],
      [
        `${type} address-level1`,
        { initial: () => getInitialValue('stateOrProvince'), commit: setFields },
      ],
      [`${type} postal-code`, { initial: () => getInitialValue('postalCode'), ...standardProps }],
      ...(type === 'billing'
        ? [
            [
              `billing country` as const,
              { initial: () => getInitialValue('countryCode'), ...standardProps } as const,
            ] as const satisfies [string, object],
          ]
        : []),
    ] satisfies Array<
      [keyof CustomerFormFields, CustomerFormSdkInterface['billing address-level1']]
    >;
  });
  return objectFromEntries(entries);
};

const getSdkInterface = (
  getCheckoutSdk: () => { state: CheckoutState; actions: CheckoutActions },
  getLocalFormValues: () => {
    [K in keyof CustomerForm]: CustomerForm[K]['value'];
  },
): CustomerFormSdkInterface => {
  const { state: checkoutState, actions: checkoutActions } = $derived(getCheckoutSdk());
  const addressForm = getAddressToSdkInterfaces(checkoutState, checkoutActions, getLocalFormValues);
  return {
    ...addressForm,
    email: {
      initial: () => orEmpty(checkoutState.customer.email),
      commit: () => checkoutActions.setEmail(getLocalFormValues()['email']),
      isValid: validEmail,
    },
    organization: {
      initial: () => orEmpty(checkoutState.customer.company?.name),
      commit: () => checkoutActions.setCompanyName(getLocalFormValues()['organization']),
      isValid: organizationNameValidator,
    },
    'vat-number': {
      initial: () => orEmpty(checkoutState.customer.company?.vatNumber?.value),
      commit: () => checkoutActions.setVatNumber(getLocalFormValues()['vat-number']),
      isValid: vatNrValidator,
    },
    'is-company': {
      initial: () => checkoutState.customer.company?.isCompany ?? false,
      commit: () => checkoutActions.setIsCompany(getLocalFormValues()['is-company']),
    },
    tel: {
      initial: () => orEmpty(checkoutState.customer.phoneNumber),
      commit: () => checkoutActions.setPhoneNumber(getLocalFormValues()['tel']),
      isValid: telNrValidator,
    },
    'shipping-same-as-billing': {
      initial: () => !(checkoutState.customer.useBillingAddress ?? false),
      commit: () =>
        checkoutActions.setUseBillingAddress(!getLocalFormValues()['shipping-same-as-billing']),
    },
    reference: {
      initial: () => orEmpty(checkoutState.customer.reference),
      commit: () => checkoutActions.setReference(getLocalFormValues()['reference']),
    },
    comment: {
      initial: () => orEmpty(checkoutState.comment),
      commit: () => checkoutActions.setComment(getLocalFormValues()['comment']),
    },
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapObject = <T extends Record<string, any>, U>(
  obj: T,
  fn: (key: keyof T, value: T[keyof T]) => U,
) =>
  Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, fn(key, value)])) as {
    [K in keyof T]: U;
  };

/* Hook */

export const useCheckoutForm = (
  getCheckoutSdk: () => { state: CheckoutState; actions: CheckoutActions },
  errors: CheckoutErrors,
  translations: ReturnType<typeof getCommonTranslations>,
) => {
  const mappedFormValues = $derived(getSdkInterface(getCheckoutSdk, () => localFormValues));

  const localFormValues = $state(mapObject(mappedFormValues, (_key, value) => value.initial())) as {
    [K in keyof CustomerForm]: CustomerForm[K]['value'];
  };

  const labels = getCustomerFormFieldLabels(translations);

  const customerFormFields = $derived(
    mapObject(mappedFormValues, (key, { initial, commit, isValid }) => {
      const localValidate = (silenceReporting: boolean = false) => {
        const validationError = isValid?.(
          localFormValues[key] as never /** hack to allow validation of string/bool*/,
          translations,
          () => localFormValues,
        );
        if (silenceReporting) {
          return validationError === undefined;
        }
        if (validationError) {
          errors.userDetailsForm[key] = validationError;
          return false;
        }
        delete errors.userDetailsForm[key];
        return true;
      };
      return {
        label: labels[key],
        get error() {
          return errors.userDetailsForm[key];
        },
        get ['value']() {
          return localFormValues[key];
        },
        set ['value'](newValue) {
          if (errors.userDetailsForm[key]) {
            delete errors.userDetailsForm[key];
          }

          (localFormValues[key] as string | boolean) = newValue;
        },
        validate: localValidate,
        onchange: () => {
          localValidate();

          commit()
            .then((x) => {
              if (!x.success) {
                errors.userDetailsForm[key] =
                  x.translatedErrorCode ?? translations.anErrorHasOccured;
              }
            })
            .catch(() => {
              errors.global = { title: translations.anErrorHasOccured, description: [] };
              localFormValues[key] = initial() as never;
            });
        },
      };
    }),
  ) as CustomerForm;
  return customerFormFields;
};

export const getCustomerFormFieldLabels = (
  translations: ReturnType<typeof getCommonTranslations>,
): Record<keyof CustomerFormFields, string> => ({
  'billing given-name': translations.firstName,
  'billing family-name': translations.lastName,
  'billing address-line1': translations.address1,
  'billing address-line2': translations.address2,
  'billing postal-code': translations.zipCode,
  'billing address-level2': translations.city,
  'billing address-level1': translations.state,
  'billing country': translations.country,
  'shipping given-name': translations.firstName,
  'shipping family-name': translations.lastName,
  'shipping address-line1': translations.address1,
  'shipping address-line2': translations.address2,
  'shipping postal-code': translations.zipCode,
  'shipping address-level2': translations.city,
  'shipping address-level1': translations.state,
  email: translations.email,
  tel: translations.phone,
  organization: translations.companyName,
  'vat-number': translations.companyVatNumber,
  'is-company': translations.purchasingAsABusiness,
  'shipping-same-as-billing': translations.billingAndShippingSameAdress,
  reference: translations.orderReference,
  comment: translations.comment,
});
